import { z } from 'zod';

/**
 * Which models the dispositif runs on (§11.1), and what one step of a protocol
 * may spend on them (§11.2).
 *
 * Same régime as before issue #61, and deliberately so: the initial values are
 * seeded from the environment at first boot, THE BASE IS THE SOURCE OF TRUTH
 * from then on, and every move is journalled with the number of sessions that
 * already existed when it happened.
 *
 * What that costs is real and is written here because it is not obvious: the
 * `INTERVIEWER_*`, `SCRIBE_*` and `NARRATOR_*` variables stop being the current
 * truth and become the seed of the FIRST boot only. Someone editing `.env` on a
 * running deploy will be fighting the base and losing, which is why
 * `.env.example` says so at the point of use.
 */

/**
 * Bumped when the SHAPE changes, not when a model moves.
 *
 * 5.0.0 (#177) - the FIFTH seat, the RANGER of scenario-succes.md §5, which
 * files the matter of a whole conversation onto the objectives a protocol
 * targets, once per session, before the analyst reads it. Major for the reason
 * 4.0.0 was: the fleet is a strict object, so a reader of 4.0.0 refuses a row
 * that carries `ranger` and a reader of 5.0.0 refuses a row that lacks it.
 * Nothing STORED is lost: `migrateStoredModelParams` recognises the four-seat
 * row at load, seeds the ranger from the ANALYST - model, pin and tariff, the
 * seat whose job it sits next to (D12) - and writes the five seats back once,
 * with its journal line.
 *
 * 4.0.0 (#149) - the FOURTH seat, the ANALYST of noyau.md §4.3, which notes the
 * six noyaux on the matter of a session once, at the close of its protocol.
 * Major for the reason 3.0.0 was: the fleet is a strict object, so a reader of
 * 3.0.0 refuses a row that carries `analyst` and a reader of 4.0.0 refuses a
 * row that lacks it. Nothing STORED is lost: `migrateStoredModelParams`
 * recognises the three-seat row at load, seeds the analyst from the SCRIBE -
 * pin and tariff, the seat whose model reads a matter rather than writes to a
 * person - and writes the four seats back once, with its journal line.
 *
 * 3.0.0 (#61) - the four places became the THREE SEATS of §11.1, each carrying
 * its own tariff, and the params gained the single AI budget of §11.2. Major,
 * and not for form: a reader of 2.0.0 cannot read a 3.0.0 row and the inverse
 * is just as true - the keys changed name, a seat gained a required member, and
 * two of the four places no longer exist. Nothing STORED is lost, because
 * `migrateStoredModelParams` in the backend reads the old shape at load and
 * writes the new one back with a journal entry - see `llm/model-params.ts`.
 *
 * 2.0.0 (BUILD-830) - the two conducting seats stopped being bare model ids
 * and became the pin the judges already were: route, model, endpoint.
 */
export const MODEL_PARAMS_SCHEMA_VERSION = '5.0.0';

/**
 * Which door a model call goes through.
 *
 * Every seat chooses, so the enum is named for what it is - the door. The
 * `judgeRouteSchema` alias left with the judges at #61: there is no measuring
 * seat any more, and an alias nobody reads is a second name for one thing.
 */
export const modelRouteSchema = z.enum(['openrouter', 'anthropic']);
export type ModelRoute = z.infer<typeof modelRouteSchema>;

/**
 * One seat's pin: route, model, and the endpoint that serves it.
 *
 * All three enter the identity of what ran, because all three change what the
 * model READS or how it is served while leaving the others untouched. The
 * endpoint included: the same open weight is served fp8 by some providers and
 * bf16 by others, and averaging across that is averaging across two
 * computations.
 *
 * Spelled as a SHAPE first, then closed and refined, because `modelSeatSchema`
 * below has to add a member to it: `.strict().refine()` yields a `ZodEffects`,
 * which cannot be extended - so the object had to exist before either of them.
 */
const seatPinShape = {
  route: modelRouteSchema,
  model: z.string().min(1),
  /**
   * OpenRouter endpoint tag. Null lets the router choose - a real option.
   *
   * `.default(null)` and not merely `.nullable()`: this shape is STORED, in
   * `model_params.params`, and a row written before the field existed carries
   * no key at all (BUILD-810). The OUTPUT type keeps the field required, so
   * absence and null stay one spelling of one sense for every caller.
   */
  provider: z.string().min(1).nullable().default(null),
};

/**
 * The same refusal `env.ts` makes at boot. Without it here, the base would be a
 * way around the boot check rather than the same rule from a second door: a
 * provider set beside the direct route is read by nobody, while suggesting a
 * guarantee against silent fallbacks that nothing enforces.
 */
const PROVIDER_ONLY_ON_ROUTER = {
  message: 'provider only applies to route=openrouter - leave it null',
  path: ['provider'],
};

export const seatPinSchema = z
  .object(seatPinShape)
  .strict()
  .refine((pin) => pin.route !== 'anthropic' || pin.provider === null, PROVIDER_ONLY_ON_ROUTER);
export type SeatPin = z.infer<typeof seatPinSchema>;

/**
 * What a million tokens costs at this seat, in euros (§11.1).
 *
 * On the SEAT and not in a table keyed by model, which is the whole change of
 * #61. The grid of `llm/prices.ts` survives as what it always was - a list of
 * published rates the repository happens to know - but it stopped being the
 * authority: an operator who pins a model nobody here has ever priced has to be
 * able to say what it costs, and until this lot the only answer was a redeploy.
 *
 * Two numbers and not four: the cache columns left with the report that read
 * them. What §11.2 needs is a euro count per step, and a step is measured on
 * what the calls of that step actually returned.
 *
 * NOT `.nonnegative()`, and that is deliberate rather than an oversight: a bound
 * in the schema answers an untyped 400 that the screen cannot word, and §11 asks
 * every refusal to be worded from its `code`. A negative rate is refused by
 * `modelParamsCoherence` with `pricing-negative`, which travels as a typed
 * refusal naming the seat - see `llm/model-params.ts`.
 */
export const seatPricingSchema = z
  .object({
    inputEurPerMillion: z.number().finite(),
    outputEurPerMillion: z.number().finite(),
  })
  .strict();
export type SeatPricing = z.infer<typeof seatPricingSchema>;

/** No rate posed. A real state, and the one the migration falls back to. */
export const ZERO_PRICING: SeatPricing = { inputEurPerMillion: 0, outputEurPerMillion: 0 };

/**
 * A seat: what it runs on, and what it costs to run (§11.1).
 *
 * `pricing` is REQUIRED and carries no default, against the habit of this
 * repository (BUILD-810), and the inversion is the point. Everywhere else a
 * defaulted field lets a row written by an older shape keep parsing; here a row
 * that keeps parsing is a row that never gets migrated, never gets its journal
 * entry, and quietly counts every call of that seat at whatever the default
 * said. The old shape must FAIL here, loudly, so that the backend recognises it
 * and rewrites it once.
 */
export const modelSeatSchema = z
  .object({ ...seatPinShape, pricing: seatPricingSchema })
  .strict()
  .refine((seat) => seat.route !== 'anthropic' || seat.provider === null, PROVIDER_ONLY_ON_ROUTER);
export type ModelSeat = z.infer<typeof modelSeatSchema>;

/**
 * A fingerprint back into a pin, or null when it is not one.
 *
 * The inverse of `seatFingerprint`, and it lives beside it for that reason: two
 * spellings of the parse would let a screen propose a pin the server reads
 * differently. It is also what lets the backend read a `model_params` row from
 * before BUILD-830, where a conducting seat was a bare model id.
 */
export function parseSeatPin(text: string): SeatPin | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  // Anchored on the two known doors rather than split on the first colon, and
  // that is not tidiness: OpenRouter model ids routinely carry a variant suffix
  // - `qwen/qwen3-235b-a22b-2507:free`, `:nitro`, `:extended` - so a leading
  // `indexOf(':')` reads `qwen/qwen3-235b-a22b-2507` as a route, finds it is
  // not one, and refuses a perfectly ordinary model id. A `model_params` row
  // holding one would then stop parsing at BOOT, which is the BUILD-810 shape
  // this function exists to avoid, on the deploys with the most history and
  // invisible to any suite starting from an empty store.
  const route = modelRouteSchema.options.find((door) => trimmed.startsWith(`${door}:`));
  // No door named: the historical spelling, which always meant the direct API.
  if (route === undefined) return { route: 'anthropic', model: trimmed, provider: null };
  const rest = trimmed.slice(route.length + 1);
  // `lastIndexOf`, because a model id may carry a slash but never an `@`.
  const at = rest.lastIndexOf('@');
  const model = at === -1 ? rest : rest.slice(0, at);
  const provider = at === -1 ? null : rest.slice(at + 1);
  if (model === '' || provider === '') return null;
  // The same rule the schema refines, applied before the parse succeeds: an
  // endpoint beside the direct route is read by nobody.
  if (route === 'anthropic' && provider !== null) return null;
  return { route, model, provider };
}

/**
 * What a seat is identified by, once the serve matters:
 * `<route>:<model>[@<endpoint>]`.
 *
 * It lives here rather than in the backend alone because BOTH sides read it:
 * the screen shows the current pin beside the new one before a swap is
 * committed, and the connection test names the pin it probed. Two spellings of
 * one format would let one of them promise a serve the other does not make.
 */
export function seatFingerprint(pin: {
  route: ModelRoute;
  model: string;
  provider?: string | null | undefined;
}): string {
  return `${pin.route}:${pin.model}${pin.provider ? `@${pin.provider}` : ''}`;
}

/**
 * The four seats of §11.1, named by the KEY of `ModelParams` each of them is.
 *
 * The four places of before #61 - two conducting seats and two judges - are
 * gone: the measure left with the cut of issue #32, and the narrator arrived
 * because §10.1 needs somebody to propose a persona's story. The ANALYST joined
 * with #149 (noyau.md §4.3): it notes the six noyaux on the matter of a
 * session, once, at the close of its protocol. What is left is exactly the
 * seats a running dispositif calls.
 *
 * The order is the order of a session: the two who hold the conversation, the
 * one who writes for the operator rather than for the person, then the two who
 * read the matter once the conversation is over - the ranger that files it
 * (issue #177), the analyst that notes it. The screen's display order derives
 * from this one.
 *
 * `turnSeatSchema` of `turn.ts` answers a different question and keeps its own
 * enum: this one names the SETTINGS an operator fills, that one names what a
 * TURN did, so it holds only the two seats a turn can call.
 */
export const modelSeatKeySchema = z.enum([
  'interviewer',
  'scribe',
  'narrator',
  'ranger',
  'analyst',
]);
export type ModelSeatKey = z.infer<typeof modelSeatKeySchema>;

export const MODEL_SEAT_KEYS = modelSeatKeySchema.options;

/**
 * The code key of the FOURTH seat, the ANALYST of noyau.md §4.3.
 *
 * A member of `modelSeatKeySchema` since #149, which widened the stored fleet
 * with its read-time migration, its `ANALYST_*` seed and the major bump of
 * `MODEL_PARAMS_SCHEMA_VERSION`. It keeps a literal of its own all the same,
 * because it is also what an analyst's CALL records (`noyauAnalystCallSchema`):
 * a determination names one seat and never another, where a seat of the fleet
 * may be any of four. The `satisfies` is what keeps the two from parting.
 */
export const ANALYST_SEAT_KEY = 'analyst' satisfies ModelSeatKey;
export const analystSeatKeySchema = z.literal(ANALYST_SEAT_KEY);
export type AnalystSeatKey = z.infer<typeof analystSeatKeySchema>;

/**
 * The code key of the FIFTH seat, the RANGER of scenario-succes.md §5 (issue
 * #177).
 *
 * A member of `modelSeatKeySchema`, with a literal of its own for the reason
 * `ANALYST_SEAT_KEY` has one: it is what a ranger's CALL records
 * (`rangerCallSchema`), and a rangement names one seat and never another.
 */
export const RANGER_SEAT_KEY = 'ranger' satisfies ModelSeatKey;
export const rangerSeatKeySchema = z.literal(RANGER_SEAT_KEY);
export type RangerSeatKey = z.infer<typeof rangerSeatKeySchema>;

/**
 * The AI budget of §11.2, in euros, on a FRESH deploy.
 *
 * POSÉ, JAMAIS CALIBRÉ - the régime of every ceiling in this repository, and
 * named as such for the same reason: an order of magnitude somebody chose is
 * honest, a figure dressed as a measurement is not. The arithmetic behind the
 * choice is written down so the next person can argue with it rather than guess
 * at it: with the notation prompt the scribe now carries - eight criteria,
 * their scales and the cumulated matter of every open objective - a READABLE
 * turn costs 0,06 to 0,12 € across the two conversing seats, so 2,50 € buys
 * between 20 and 41 turns on ONE step (2,50 / 0,12 and 2,50 / 0,06, the cheap
 * end being the one that matters: a step is capped when the budget is PASSED,
 * so the 41st turn of a cheap step is the first over). A step that reaches it
 * has run away; a step doing its job never sees it, and a protocol step is
 * bounded by its own `maxTurns` long before the budget is.
 *
 * That direction is the whole point of the number. A ceiling that cuts normal
 * work teaches the team to raise it without reading it, and a ceiling nobody
 * reads protects nothing.
 *
 * ## RECALIBRATED on 2026-09-18, and why the old figure was not wrong
 *
 * It was 0,50 € for a turn priced at 0,014 €, which bought 35 turns at the
 * prices of the day - the same order of magnitude, for the same reason. The
 * scribe's prompt then grew (#80: the bands, the instruction and the examples
 * of every frozen criterion), a turn went up by a factor of five to eight, and
 * the number stopped meaning what its own comment said: at 0,06 to 0,12 € a
 * turn, twelve turns of a single step cost 0,72 to 1,44 €, so a step doing its
 * job PASSED the old ceiling - between its fifth turn (0,50 / 0,12 = 4,2) and
 * its ninth (0,50 / 0,06 = 8,3). It closed one in production on its fifth turn
 * out of twelve, with the fallback text and no empty answer (issue #97), which
 * is the expensive end of that same arithmetic. The cap semantics are unchanged
 * (§8.3 point 3, §11.2): what moved is the figure and what a turn costs today.
 *
 * A deploy that already holds a value in `model_params` keeps it - this is the
 * default of a FRESH store, not a migration - so an operator has to check
 * Réglages > Modèles on any store that was booted before this change.
 */
export const DEFAULT_BUDGET_PER_STEP_EUR = 2.5;

export const modelParamsSchema = z
  .object({
    /** Semver, bumped on every write. The base is the source of truth. */
    version: z.string().min(1),
    /**
     * The five seats of §11.1, under the names §11.1 gives them.
     *
     * They are keys of a stored JSON column, so renaming them is exactly the
     * kind of change that breaks a base with history and no empty-store suite -
     * which is why the rename of #61 comes WITH a content migration read at
     * load rather than alone (`llm/model-params.ts`), and why the analyst of
     * #149 does too: REQUIRED like the three others, so a three-seat row fails
     * here, loudly, and is recognised and rewritten once rather than read with
     * a seat nobody seeded. The ranger of #177 follows the same rule.
     */
    interviewer: modelSeatSchema,
    scribe: modelSeatSchema,
    narrator: modelSeatSchema,
    ranger: modelSeatSchema,
    analyst: modelSeatSchema,
    /**
     * ONE value in euros, applied to EACH STEP (§11.2, hypothèse H3): when the
     * cumulated cost of a step's calls passes it, the step is at its ceiling
     * (§8.3). Not a budget per session, and not a budget per seat.
     *
     * `.default()` rather than required, the ordinary régime of an added field
     * here (BUILD-810): a caller a deploy behind sends a body without it and
     * gets the posed ceiling rather than a view that fails to parse. The OUTPUT
     * type keeps it required, so absence and default are one sense.
     *
     * Finite rather than positive, for the reason `seatPricingSchema` gives: a
     * bound in the schema is an untyped 400, and §11 words every refusal from
     * its code. A negative budget is refused with `budget-negative`.
     */
    budgetPerStepEur: z.number().finite().default(DEFAULT_BUDGET_PER_STEP_EUR),
  })
  .strict();
export type ModelParams = z.infer<typeof modelParamsSchema>;

/** The three settings of a seat's pin, spelled once for both sides of the wire. */
export function modelSeatPath(
  seat: ModelSeatKey,
  field: 'route' | 'model' | 'provider',
): ModelTextPath {
  return `${seat}.${field}` as ModelTextPath;
}

/** The two tariffs of a seat, likewise. */
export function modelPricingPath(
  seat: ModelSeatKey,
  field: 'inputEurPerMillion' | 'outputEurPerMillion',
): ModelNumericPath {
  return `${seat}.pricing.${field}` as ModelNumericPath;
}

/** A whole seat, as the one shape every reader uses. */
export function seatOfParams(params: ModelParams, seat: ModelSeatKey): ModelSeat {
  return params[seat];
}

/** What a seat is pinned to, without its tariff. */
export function seatPinOfParams(params: ModelParams, seat: ModelSeatKey): SeatPin {
  const { route, model, provider } = params[seat];
  return { route, model, provider };
}

/** What a seat charges. */
export function seatPricingOfParams(params: ModelParams, seat: ModelSeatKey): SeatPricing {
  return params[seat].pricing;
}

/**
 * The twenty-six settings, in the order the screen shows them.
 *
 * ONE list, read by the three places that would otherwise drift apart: the
 * write path (`llm/model-params.ts` diffs exactly these paths and journals what
 * moved), the route that serves them, and the panel that edits them. A setting
 * added to `ModelParams` and forgotten here would be a setting nobody can move
 * and nobody sees moved - so the shape test asserts the two agree.
 *
 * `seat` is now the seat KEY rather than a legend of its own: with four seats
 * and one card each, a second vocabulary for grouping would be a second name
 * for the same thing. The budget belongs to no seat and says so with `null`.
 *
 * The French sits here because both sides of the wire read it, and a second
 * spelling would be a second meaning.
 */
export const MODEL_SETTINGS = [
  { path: 'interviewer.route', seat: 'interviewer', label: 'la porte de l’interviewer' },
  { path: 'interviewer.model', seat: 'interviewer', label: 'le modèle de l’interviewer' },
  { path: 'interviewer.provider', seat: 'interviewer', label: 'l’hébergeur de l’interviewer' },
  {
    path: 'interviewer.pricing.inputEurPerMillion',
    seat: 'interviewer',
    label: 'le tarif d’entrée de l’interviewer',
  },
  {
    path: 'interviewer.pricing.outputEurPerMillion',
    seat: 'interviewer',
    label: 'le tarif de sortie de l’interviewer',
  },
  { path: 'scribe.route', seat: 'scribe', label: 'la porte du scribe' },
  { path: 'scribe.model', seat: 'scribe', label: 'le modèle du scribe' },
  { path: 'scribe.provider', seat: 'scribe', label: 'l’hébergeur du scribe' },
  {
    path: 'scribe.pricing.inputEurPerMillion',
    seat: 'scribe',
    label: 'le tarif d’entrée du scribe',
  },
  {
    path: 'scribe.pricing.outputEurPerMillion',
    seat: 'scribe',
    label: 'le tarif de sortie du scribe',
  },
  { path: 'narrator.route', seat: 'narrator', label: 'la porte du narrateur' },
  { path: 'narrator.model', seat: 'narrator', label: 'le modèle du narrateur' },
  { path: 'narrator.provider', seat: 'narrator', label: 'l’hébergeur du narrateur' },
  {
    path: 'narrator.pricing.inputEurPerMillion',
    seat: 'narrator',
    label: 'le tarif d’entrée du narrateur',
  },
  {
    path: 'narrator.pricing.outputEurPerMillion',
    seat: 'narrator',
    label: 'le tarif de sortie du narrateur',
  },
  { path: 'ranger.route', seat: 'ranger', label: 'la porte du rangeur' },
  { path: 'ranger.model', seat: 'ranger', label: 'le modèle du rangeur' },
  { path: 'ranger.provider', seat: 'ranger', label: 'l’hébergeur du rangeur' },
  {
    path: 'ranger.pricing.inputEurPerMillion',
    seat: 'ranger',
    label: 'le tarif d’entrée du rangeur',
  },
  {
    path: 'ranger.pricing.outputEurPerMillion',
    seat: 'ranger',
    label: 'le tarif de sortie du rangeur',
  },
  { path: 'analyst.route', seat: 'analyst', label: 'la porte de l’analyste' },
  { path: 'analyst.model', seat: 'analyst', label: 'le modèle de l’analyste' },
  { path: 'analyst.provider', seat: 'analyst', label: 'l’hébergeur de l’analyste' },
  {
    path: 'analyst.pricing.inputEurPerMillion',
    seat: 'analyst',
    label: 'le tarif d’entrée de l’analyste',
  },
  {
    path: 'analyst.pricing.outputEurPerMillion',
    seat: 'analyst',
    label: 'le tarif de sortie de l’analyste',
  },
  { path: 'budgetPerStepEur', seat: null, label: 'le budget IA par étape' },
] as const satisfies readonly { path: string; seat: ModelSeatKey | null; label: string }[];

export type ModelSettingPath = (typeof MODEL_SETTINGS)[number]['path'];

/** The settings whose value is TEXT: a door, a model id, an endpoint. */
export type ModelTextPath = Extract<
  ModelSettingPath,
  `${string}.route` | `${string}.model` | `${string}.provider`
>;

/** The settings whose value is a NUMBER of euros: the tariffs and the budget. */
export type ModelNumericPath = Exclude<ModelSettingPath, ModelTextPath>;

/**
 * The path a shape migration journals under (#61).
 *
 * It is not a setting - nobody moves it from the screen, and it is deliberately
 * absent from `MODEL_SETTINGS` for that reason - but it IS a journal line, and
 * the journal is read by a human on the screen that writes it. Spelled here so
 * the backend that writes it and the screen that words it agree.
 */
export const MODEL_SHAPE_PATH = 'schema';

function pathKeys(path: string): string[] {
  return path.split('.');
}

function readIn(target: unknown, keys: readonly string[]): unknown {
  let current = target;
  for (const key of keys) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function writeIn(target: unknown, keys: readonly string[], value: unknown): unknown {
  const [head, ...rest] = keys;
  if (head === undefined) return value;
  if (target === null || typeof target !== 'object') {
    throw new Error(`Unknown model setting path segment: ${head}`);
  }
  const record = target as Record<string, unknown>;
  if (rest.length === 0) return { ...record, [head]: value };
  return { ...record, [head]: writeIn(record[head], rest, value) };
}

/**
 * Reads one setting off the params, as the TEXT the journal stores.
 *
 * `null` is a value and not an absence - an unpinned endpoint is a deliberate
 * choice - so a missing path THROWS rather than coming back indistinguishable
 * from one. A euro figure comes back as its decimal spelling, which is what
 * makes the diff of `updateModelParams` one comparison rather than two: a
 * tariff that moved and a model that moved are the same kind of journal line.
 */
export function readModelSetting(params: ModelParams, path: ModelSettingPath): string | null {
  const value = readIn(params, pathKeys(path));
  if (value === null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  throw new Error(`Unknown model setting path: ${path}`);
}

/** ... and writes one back, without mutating what it was handed. */
export function writeModelSetting(
  params: ModelParams,
  path: ModelTextPath,
  value: string | null,
): ModelParams {
  return writeIn(params, pathKeys(path), value) as ModelParams;
}

/**
 * The euro figure of a numeric setting, as a number.
 *
 * Separate from `readModelSetting` rather than a widened return type, and the
 * split earns its keep twice: the journal only ever wants text, and the panel
 * only ever wants a number to compute an estimate with. A single function
 * returning `string | number | null` would make both callers narrow a union
 * that the PATH already decides.
 */
export function readModelNumber(params: ModelParams, path: ModelNumericPath): number {
  const value = readIn(params, pathKeys(path));
  if (typeof value !== 'number') throw new Error(`Unknown model setting path: ${path}`);
  return value;
}

export function writeModelNumber(
  params: ModelParams,
  path: ModelNumericPath,
  value: number,
): ModelParams {
  return writeIn(params, pathKeys(path), value) as ModelParams;
}

/**
 * One line of the journal. Displayed in the screen, not merely stored: free
 * editing removes the pre-registration property of the pinned models, and a
 * dated history is what replaces it.
 */
export const modelParamChangeSchema = z
  .object({
    /** The version this change produced. */
    paramsVersion: z.string().min(1),
    changedAt: z.string().datetime(),
    /** Name typed into the form - there are no accounts. */
    changedBy: z.string().min(1),
    /** Dotted path of the single setting this entry moved, e.g. 'scribe.model'. */
    path: z.string().min(1),
    /** Text values, and `''` for an endpoint that was not pinned either side. */
    from: z.string(),
    to: z.string(),
    /**
     * How many sessions already existed. THE field that carries the honesty of
     * the move: a model changed at zero sessions is a calibration, the same
     * change at fifteen is something else, and the reader must be able to judge
     * for themselves.
     */
    sessionCountAtChange: z.number().int().nonnegative(),
    note: z.string().nullable(),
  })
  .strict();
export type ModelParamChange = z.infer<typeof modelParamChangeSchema>;

/**
 * Something worth saying about a seat, said without blocking the write.
 *
 * ACCEPTED, never refused - a whitelist would block the next model release
 * behind a redeploy, which is the exact problem this screen exists to remove.
 * The screen says so in French from the `code`; the English here is a
 * developer's string.
 */
export const modelWarningSchema = z
  .object({
    path: z.string().min(1),
    model: z.string().min(1),
    code: z.enum([
      'unknown-sampling-support',
      /**
       * The model id is not spelled the way this door names its models: the
       * router prefixes with a vendor (`anthropic/claude-x`), the direct API
       * does not (`claude-x`). Pinning one against the other is a 404 or a 400
       * on EVERY call of that seat.
       *
       * A WARNING and never a refusal, for the reason `unknown-sampling-support`
       * is one: the rule reads a naming convention, and a convention is exactly
       * the kind of thing the next provider breaks.
       */
      'route-model-mismatch',
      /**
       * This seat charges nothing (#61, §11.1, §11.2).
       *
       * Which is a real, reachable state and not a corruption: the migration
       * falls back to zero for a model `PRICE_TABLE` has never heard of, and an
       * operator can type a zero. It matters enough to say out loud because it
       * disarms §11.2 silently - a seat at zero contributes nothing to the
       * cumulated cost of a step, so the budget can never be reached and the
       * ceiling that was supposed to stop a runaway never fires.
       *
       * A warning and not a refusal, deliberately: a rate somebody has not
       * looked up yet is an ordinary intermediate state of this screen, and
       * refusing it would make the seat unsaveable until a price list is at
       * hand.
       */
      'pricing-missing',
    ]),
  })
  .strict();
export type ModelWarning = z.infer<typeof modelWarningSchema>;

/**
 * Why a write was refused. Three codes, each a configuration that is wrong in a
 * way a click can reach:
 *
 * - `sampling-not-supported`: a model that removed the sampling parameters,
 *   pinned to a seat whose committed sampling sends a number. A 400 on EVERY
 *   call of that seat - the BUILD-634 incident, which must not be reachable by
 *   a click.
 * - `pricing-negative` / `budget-negative` (#61): a negative euro figure. The
 *   bound lives HERE rather than in the schema on purpose - `z.number()` would
 *   answer an untyped 400 the screen cannot word, and §11 asks every refusal to
 *   be worded from its code.
 */
export const modelParamErrorSchema = z
  .object({
    path: z.string().min(1),
    /**
     * The model id the refusal is about, or `''` when it is about no model.
     *
     * Empty rather than nullable: the budget of §11.2 belongs to no seat, so
     * there is no id to name, and a second spelling of « rien à nommer » would
     * be one more thing for the screen to narrow. It prints the parenthetical
     * only when there is something in it.
     */
    model: z.string(),
    code: z.enum(['sampling-not-supported', 'pricing-negative', 'budget-negative']),
    /** Developer string: logs and OpenAPI, never shown to an operator as-is. */
    message: z.string().min(1),
  })
  .strict();
export type ModelParamError = z.infer<typeof modelParamErrorSchema>;

export const modelParamsUpdateSchema = z
  .object({
    params: modelParamsSchema,
    /** Name typed into the form - there are no accounts. */
    changedBy: z.string().min(1),
    note: z.string().nullable().optional(),
  })
  .strict();
export type ModelParamsUpdate = z.infer<typeof modelParamsUpdateSchema>;

/**
 * What one session at one seat would cost, in euros, AT THAT SEAT'S OWN TARIFF
 * (§11.1).
 *
 * INDICATIVE, and the word is load-bearing: it is a posed token profile priced
 * through the rates the operator is holding in the form, not a measurement.
 * What a session actually costs is what its turns record (§8.7). This exists so
 * a seat card can carry an order of magnitude at the moment somebody picks a
 * model, which is the only moment the figure can still change a mind.
 *
 * One figure per SEAT and no longer one per (seat, model): since #61 the rate
 * belongs to the seat, so there is nothing to say about a model the seat is not
 * running.
 */
export const modelCostEstimateSchema = z
  .object({
    seat: modelSeatKeySchema,
    costEur: z.number().nonnegative(),
  })
  .strict();
export type ModelCostEstimate = z.infer<typeof modelCostEstimateSchema>;

/**
 * The token profile of ONE typical session, per seat. POSÉ, JAMAIS CALIBRÉ -
 * the régime of every ceiling in this repository, and named as such for the
 * same reason: an order of magnitude somebody chose is honest, a figure dressed
 * as a measurement is not.
 *
 * Cache tokens are folded into `inputTokens` rather than kept apart, because a
 * seat carries TWO rates (§11.1) and there is no third number to price a cache
 * read with. That over-estimates on the direct API, where a cache read is a
 * tenth of an input token - and over-estimating is the right direction for a
 * figure that sits next to a ceiling.
 *
 * The numbers carry the facts that make one profile for four seats wrong:
 *
 *  - a conversing seat pays its system prompt once and re-reads it every turn,
 *    which is why the input column dwarfs the output one;
 *  - the SCRIBE outputs more than the interviewer over a session - a question
 *    is one sentence, its contract (§8.2) is a verbose JSON envelope;
 *  - the NARRATOR is called ONCE, from a button, with no conversation behind
 *    it. It is the cheapest seat by an order of magnitude, and a profile
 *    borrowed from a conversing seat would have said the opposite.
 *  - the RANGER (issue #177) is called once per session and retried at most
 *    once (scenario-succes.md §3.4): its output is a JSON of extracts and
 *    formulas, three thousand tokens being a margin over six objectives with a
 *    few verbatim extracts each. Its INPUT is the whole conversation, which the
 *    analyst never reads; the analyst's figure is posed here as a starting
 *    point (POSÉ, JAMAIS CALIBRÉ) and the first real sessions will say how far
 *    it is.
 *  - the ANALYST is called once too, and retried at most once (noyau.md §4.5):
 *    its input is the grid and the matter of one session, a few thousand
 *    tokens, and its output six notes with a sentence each - plus the
 *    thinking it does at its model's default (§4.3, N3), which a Claude 5 model
 *    bills as output, so the output column carries a margin the six sentences
 *    alone would not need.
 *
 * It lives in `shared` and not on the backend alone because BOTH sides compute
 * with it: the route serves a figure per seat for a `curl`, and the screen
 * recomputes one live while somebody is still typing a tariff. A second profile
 * in the browser would let the card and the API disagree about the same seat.
 */
export const TYPICAL_SESSION_TOKENS: Record<
  ModelSeatKey,
  { inputTokens: number; outputTokens: number }
> = {
  interviewer: { inputTokens: 126_000, outputTokens: 1_500 },
  scribe: { inputTokens: 97_500, outputTokens: 6_000 },
  narrator: { inputTokens: 3_000, outputTokens: 1_200 },
  ranger: { inputTokens: 6_000, outputTokens: 3_000 },
  analyst: { inputTokens: 6_000, outputTokens: 2_500 },
};

/**
 * The indicative cost of one session at one seat, at that seat's own tariff.
 *
 * Rounded to the cent, like every euro figure of this repository, so that
 * summing seats and reading one seat never disagree by a rounding.
 *
 * Zero is a real answer and not a hole: a seat whose tariff nobody has posed
 * genuinely costs nothing as far as §11.2 is concerned - the budget of a step
 * never sees it - and that is said by the `pricing-missing` warning rather than
 * by a null the screen would have to word a second time.
 */
export function estimateSeatCostEur(pricing: SeatPricing, seat: ModelSeatKey): number {
  const profile = TYPICAL_SESSION_TOKENS[seat];
  const euros =
    (profile.inputTokens * pricing.inputEurPerMillion +
      profile.outputTokens * pricing.outputEurPerMillion) /
    1_000_000;
  return Math.max(0, Math.round(euros * 100) / 100);
}

/**
 * A published rate the repository happens to know, offered as a PREFILL.
 *
 * Served by the backend rather than copied into the browser, and that is the
 * whole point of the field: the grid is one list in `llm/prices.ts`, so a
 * second copy of the rates in the front would be a second spelling of what a
 * call costs and the two would drift on the first rate that moves.
 *
 * A suggestion and never an authority: the seat's own `pricing` is what counts
 * the euros, and an operator is free to type something else. Without it,
 * picking another model would leave the previous model's rate behind - a wrong
 * euro count that nothing on the screen could point at.
 */
export const modelTariffSchema = z
  .object({
    model: z.string().min(1),
    inputEurPerMillion: z.number().nonnegative(),
    outputEurPerMillion: z.number().nonnegative(),
  })
  .strict();
export type ModelTariff = z.infer<typeof modelTariffSchema>;

/** What `GET /api/models/params` serves: the seats and their history, together. */
export const modelParamsViewSchema = z
  .object({
    params: modelParamsSchema,
    /** The environment seed of the first boot - the left column of the panel. */
    initial: modelParamsSchema,
    journal: z.array(modelParamChangeSchema),
    /** Sessions right now: what the next move would be stamped with. */
    sessionCount: z.number().int().nonnegative(),
    /** Seats worth a sentence. Informative: the write went through. */
    warnings: z.array(modelWarningSchema),
    /**
     * The indicative cost of one session, per seat, at the seat's own tariff.
     *
     * `.default([])` rather than required, for the reason every added field in
     * this repository is defaulted (BUILD-810): a caller a deploy behind sends a
     * body without it, and the cards simply carry no figure instead of the whole
     * view failing to parse.
     */
    estimates: z.array(modelCostEstimateSchema).default([]),
    /** The published rates this deploy knows, to prefill a seat that moves. */
    tariffs: z.array(modelTariffSchema).default([]),
  })
  .strict();
export type ModelParamsView = z.infer<typeof modelParamsViewSchema>;

/**
 * What a connection test found at one seat.
 *
 * A CLOSED set of codes, because the screen words every one of them in French
 * from the `code` and never from a server message - and the `Record` on the
 * front is what makes an eighth code impossible to add without its sentence.
 *
 * Three of them are decided WITHOUT calling anything, and that is deliberate: a
 * missing key, an incoherent sampling pair and a stubbed deploy are facts about
 * the configuration, knowable without a provider, and spending a real call to
 * rediscover them would only add a network to a question that has none.
 */
export const modelTestCodeSchema = z.enum([
  /** The call went out and came back. */
  'ok',
  /** This deploy holds no key for the door this seat names. No call made. */
  'missing-key',
  /** The provider does not serve this model id - a 404 or a 400 naming it. */
  'unknown-model',
  /**
   * The model removed the sampling parameters and this seat sends a number: a
   * 400 on EVERY call (BUILD-634). No call made - the pair is refused on sight,
   * exactly as `PUT /api/models/params` refuses to write it.
   */
  'sampling-incoherent',
  /** Anything else the door answered, or did not: a status, a network error. */
  'unreachable',
  /** The call was still open when the short probe budget ran out. */
  'timeout',
  /**
   * This deploy calls no provider at all - `LLM_MODE=stub`. NOTHING was called,
   * so this is neither a success nor a failure: it is the absence of a test,
   * and it is a code of its own rather than a green `ok` because a panel that
   * reports « Réussi » on a model id nobody serves teaches the eye that this
   * button proves something it cannot prove here.
   *
   * Decided from the DEPLOY and never guessed by the screen: the backend is the
   * only side that knows which mode it resolved.
   */
  'stub',
]);
export type ModelTestCode = z.infer<typeof modelTestCodeSchema>;

export const modelTestStatusSchema = z.enum(['ok', 'warning', 'error', 'skipped']);
export type ModelTestStatus = z.infer<typeof modelTestStatusSchema>;

/**
 * How severe each outcome is, decided once and sent rather than derived twice.
 *
 * The split is by CONSEQUENCE and not by how alarming the word sounds: an
 * `error` means every call of that seat would fail for a reason that is in the
 * configuration, a `warning` means the provider did not answer THIS TIME. Only
 * `timeout` is a warning, because the probe budget is deliberately far shorter
 * than the sixty seconds a real conducting call gets - a probe that runs out
 * proves the door is slow, never that the pin is wrong.
 *
 * `skipped` is a FOURTH severity rather than a shade of the other three, and it
 * is worth the enum entry: a stubbed deploy learnt nothing, which is neither
 * « ça tient » nor « ça a échoué ».
 */
export const MODEL_TEST_STATUS: Record<ModelTestCode, ModelTestStatus> = {
  ok: 'ok',
  'missing-key': 'error',
  'unknown-model': 'error',
  'sampling-incoherent': 'error',
  unreachable: 'error',
  timeout: 'warning',
  stub: 'skipped',
};

export const modelTestResultSchema = z
  .object({
    seat: modelSeatKeySchema,
    /** The pin actually probed: the FORM's, never what the base holds. */
    pin: seatPinSchema,
    code: modelTestCodeSchema,
    status: modelTestStatusSchema,
    /** Round trip in ms, or null when no call was made. */
    latencyMs: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type ModelTestResult = z.infer<typeof modelTestResultSchema>;

/**
 * What `POST /api/models/test` is handed: the four seats AS THE FORM HOLDS
 * THEM, unsaved.
 *
 * Testing what the base holds would answer the wrong question - the operator is
 * about to write something else, and the whole value of the button is finding
 * out before the write rather than after the first personne. Nothing is written
 * and nothing is journalled: this route reads a provider, it does not move a
 * model.
 */
export const modelTestRequestSchema = z.object({ params: modelParamsSchema }).strict();
export type ModelTestRequest = z.infer<typeof modelTestRequestSchema>;

export const modelTestViewSchema = z.object({ results: z.array(modelTestResultSchema) }).strict();
export type ModelTestView = z.infer<typeof modelTestViewSchema>;

/**
 * The Claude families the models screen offers a tile for (#175).
 *
 * Mythos is absent on purpose: it is served to an access programme and not to
 * an account, so a tile for it would be a 404 on every call of whoever picks it.
 */
export const claudeFamilySchema = z.enum(['opus', 'sonnet', 'haiku', 'fable']);
export type ClaudeFamily = z.infer<typeof claudeFamilySchema>;

/**
 * The spelling the SAME weights share across both doors, or null when the id
 * is not a Claude model of an offered family.
 *
 * The direct API writes `claude-haiku-4-5-20251001`, the router
 * `anthropic/claude-haiku-4.5`: the vendor prefix goes, the router's dots fold
 * into dashes, and a trailing date snapshot is dropped. What is left -
 * `claude-haiku-4-5` - is what pairs a tile with its twin on the other door.
 * A router variant (`:batch`, `:thinking`) is NOT a Claude model here: it is
 * another serve at another price, and the screen offers the plain one.
 */
export function claudeModelKey(model: string): string | null {
  const bare = model.startsWith('anthropic/') ? model.slice('anthropic/'.length) : model;
  const match = /^claude-(opus|sonnet|haiku|fable)-(\d+(?:[.-]\d{1,2})?)(?:-\d{8})?$/.exec(bare);
  if (match === null) return null;
  const [, family, version] = match;
  return `claude-${family}-${(version ?? '').replaceAll('.', '-')}`;
}

/** The family of a Claude model id, on either door. */
export function claudeFamilyOf(model: string): ClaudeFamily | null {
  const key = claudeModelKey(model);
  if (key === null) return null;
  const parsed = claudeFamilySchema.safeParse(key.split('-')[1]);
  return parsed.success ? parsed.data : null;
}

/**
 * One tile the models screen can offer, as a PROVIDER lists it today (#175).
 *
 * Read from the providers' own model lists rather than written in the front:
 * until #175 a new model was one deploy away, which is the problem §11.1 exists
 * to remove. Still NOT a whitelist - free entry stays.
 */
export const modelCatalogEntrySchema = z
  .object({
    route: modelRouteSchema,
    /** The id exactly as this door names it. */
    model: z.string().min(1),
    /** What the provider calls it, vendor prefix removed. */
    label: z.string().min(1),
    family: claudeFamilySchema,
    /** `claudeModelKey`: equal on both doors for the same weights. */
    key: z.string().min(1),
    releasedAt: z.string().datetime(),
    /**
     * The published rate, in euros, when a provider list carries one.
     *
     * The Models API of Anthropic publishes no price, so a direct id borrows
     * its router twin's list price - the same public rate - and falls back to
     * the repository grid. Null when nobody knows: the tile then leaves the
     * seat's tariff alone rather than inventing one.
     */
    pricing: seatPricingSchema.nullable(),
  })
  .strict();
export type ModelCatalogEntry = z.infer<typeof modelCatalogEntrySchema>;

/**
 * How a door's list was obtained.
 *
 * - `live`: read from the provider within the cache window.
 * - `stub`: `LLM_MODE=stub`, nothing is called - the screen shows its own list.
 * - `unavailable`: no key for this door, or the provider did not answer.
 */
export const modelCatalogStatusSchema = z.enum(['live', 'stub', 'unavailable']);
export type ModelCatalogStatus = z.infer<typeof modelCatalogStatusSchema>;

/** What `GET /api/models/catalog` serves: the newest model of each family, per door. */
export const modelCatalogViewSchema = z
  .object({
    doors: z
      .object({
        anthropic: modelCatalogStatusSchema,
        openrouter: modelCatalogStatusSchema,
      })
      .strict(),
    /** When the lists were read, or null when none was. */
    fetchedAt: z.string().datetime().nullable(),
    entries: z.array(modelCatalogEntrySchema),
  })
  .strict();
export type ModelCatalogView = z.infer<typeof modelCatalogViewSchema>;
